import { useEffect, useState } from 'react';
import { useGetIdentity } from 'react-admin';
import { Box, Button, Typography } from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { stopImpersonationOnServer } from '../authProvider';
import { usageRecorder } from '../usage/usageRecorder';
import {
  IMPERSONATE_ENDED_URL,
  endImpersonationTab,
  impersonationInfo,
  markImpersonationActive,
  type ImpersonationInfo,
} from './sessionToken';
import { clientTime } from '../shared/dateTime';

/** Treść paska: kto pracuje, kto się zalogował i do kiedy trwa sesja. */
export function impersonationBannerText(info: ImpersonationInfo, targetName: string | null | undefined): string {
  const who = targetName && targetName.trim() !== '' ? targetName : info.target.username;
  const by = info.impersonator.name || info.impersonator.username;
  const until = info.expiresAt ? ` Sesja wygaśnie o ${clientTime(new Date(info.expiresAt).toISOString())}.` : '';
  return `Pracujesz jako ${who} — zalogowano przez ${by}.${until}`;
}

/**
 * Stały pasek karty „Zaloguj jako” (decyzja Bartka 02.11.2026): widać, że
 * to nie Twoje konto i kto się zalogował. „Zakończ” kończy przejęcie na
 * serwerze, czyści sesję tej karty i zamyka ją (gdy przeglądarka nie pozwala
 * — pokazuje „sesja zakończona”). Karta administratora zostaje nietknięta.
 * Poza kartą przejęcia nic nie rysuje.
 */
export const ImpersonationBanner = () => {
  const info = impersonationInfo();
  const { identity } = useGetIdentity();
  const [busy, setBusy] = useState(false);
  const sessionId = info?.sessionId ?? null;
  const targetName = identity?.fullName || info?.target.username || '';
  const expiresAt = info?.expiresAt ?? null;
  // Inne karty tej przeglądarki widzą, że trwa praca jako ten użytkownik
  // (ostrzeżenie w karcie bez sesji przejęcia, przegląd #582 D5).
  useEffect(() => {
    if (sessionId && expiresAt) markImpersonationActive({ sessionId, target: targetName, expiresAt });
  }, [sessionId, targetName, expiresAt]);
  if (!info) return null;

  const end = async () => {
    setBusy(true);
    // Zdarzenia pomiaru z tej karty wychodzą jeszcze z tokenem przejęcia.
    usageRecorder.endSession();
    await stopImpersonationOnServer();
    endImpersonationTab();
    window.close();
    // Karta otwarta ręcznie albo przeglądarka nie zamknęła — „sesja zakończona”.
    window.location.replace(IMPERSONATE_ENDED_URL);
  };

  return (
    <Box
      role="status"
      data-testid="impersonation-banner"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 2,
        py: 0.75,
        mb: 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        borderRadius: 1,
      }}
    >
      <SupervisorAccountIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600, flex: '1 1 260px', minWidth: 0 }}>
        {impersonationBannerText(info, identity?.fullName)}
      </Typography>
      <Button
        size="small"
        variant="contained"
        color="inherit"
        onClick={() => void end()}
        disabled={busy}
        data-testid="impersonation-end"
        sx={{ color: 'text.primary' }}
      >
        Zakończ
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
